// codebaseAnalyzer — Pure analys av filträd → ModuleGraph. Används av
// diagramFromCodebase-skillen för att bygga ett multi-sheet-diagram av
// repo-struktur. Helt utan side-effekter — input är list av paths +
// (optional) deras content-strängar; output är gruppering + import-edges.
//
// Tester injicerar fil-listan; AA1c (codebaseLoader) wrappa import.meta.glob
// för att producera den listan i runtime.

export interface FileNode {
  /** Full path från projekt-root, t.ex. "src/lib/aiDiagramTools.ts". */
  path: string;
  /** Optional file-content för import-extraction. Saknas → inga edges
   * härifrån (men noden räknas fortfarande). */
  content?: string;
}

export interface ModuleNode {
  /** Top-level folder (t.ex. "lib", "components", "hooks"). */
  module: string;
  /** Filer i denna module. */
  files: FileNode[];
}

export interface ImportEdge {
  /** Källfil-path. */
  from: string;
  /** Målfil-path eller modul-prefix. */
  to: string;
  /** True om edge:n går till en annan top-level-modul. */
  crossModule: boolean;
}

export interface ModuleGraph {
  modules: ModuleNode[];
  /** Alla imports vi kunde matcha till en känd path. */
  edges: ImportEdge[];
  totalFiles: number;
}

/**
 * Hitta top-level modul från en path. Stripa `src/`-prefix om present
 * eftersom det är konvention i denna repo.
 *
 *   src/lib/foo.ts          → "lib"
 *   src/components/x/y.ts   → "components"
 *   pages/Home.tsx          → "pages"
 *   single-file.ts          → "(root)"
 */
export function topLevelModule(path: string): string {
  const stripped = path.replace(/^(\.\/)?src\//, '');
  const segments = stripped.split('/').filter(Boolean);
  if (segments.length <= 1) return '(root)';
  return segments[0];
}

/**
 * Gruppera fil-listan per top-level-modul. Bevarar input-ordningen inom
 * varje modul. Returnerar moduler i alfabetisk ordning.
 */
export function groupByModule(files: FileNode[]): ModuleNode[] {
  const map = new Map<string, FileNode[]>();
  for (const f of files) {
    const mod = topLevelModule(f.path);
    if (!map.has(mod)) map.set(mod, []);
    map.get(mod)!.push(f);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([module, files]) => ({ module, files }));
}

const IMPORT_REGEX = /(?:^|\n)\s*import\s+(?:[\w*\s{},]+\s+from\s+)?['"]([^'"]+)['"]/g;
const ALIAS_PREFIX = '@/';

/**
 * Extrahera importerade specifiers från en filcontent-sträng. Returnerar
 * råa source-strängar som de står i koden ('@/lib/x', '../utils', osv).
 */
export function extractImports(content: string): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  IMPORT_REGEX.lastIndex = 0;
  while ((m = IMPORT_REGEX.exec(content)) !== null) {
    out.push(m[1]);
  }
  return out;
}

/**
 * Slå upp en import-specifier mot file-tree:t. Stödjer:
 *  - alias `@/lib/foo`  → `src/lib/foo.ts` om present
 *  - relativ `../foo`   → resolved mot `fromPath`
 *  - bare `react`       → ignoreras (extern)
 *
 * Returnerar matchad fil-path eller null. Trial:ar suffix .ts/.tsx/index.ts.
 */
export function resolveImport(
  spec: string,
  fromPath: string,
  knownPaths: Set<string>,
): string | null {
  // Skip externa imports (utan @/ eller ./ eller ../).
  const isAlias = spec.startsWith(ALIAS_PREFIX);
  const isRelative = spec.startsWith('./') || spec.startsWith('../');
  if (!isAlias && !isRelative) return null;

  let baseRel: string;
  if (isAlias) {
    baseRel = `src/${spec.slice(ALIAS_PREFIX.length)}`;
  } else {
    // Relativ från fromPath:s katalog.
    const fromDir = fromPath.split('/').slice(0, -1).join('/');
    const parts = (fromDir + '/' + spec).split('/');
    const stack: string[] = [];
    for (const p of parts) {
      if (p === '' || p === '.') continue;
      if (p === '..') stack.pop();
      else stack.push(p);
    }
    baseRel = stack.join('/');
  }

  const candidates = [
    baseRel,
    `${baseRel}.ts`,
    `${baseRel}.tsx`,
    `${baseRel}/index.ts`,
    `${baseRel}/index.tsx`,
  ];
  for (const c of candidates) {
    if (knownPaths.has(c)) return c;
  }
  return null;
}

/**
 * Bygg full ModuleGraph från file-listan. Beräknar både modul-grupper
 * och alla import-edges (där content finns).
 */
export function analyzeFileTree(files: FileNode[]): ModuleGraph {
  const modules = groupByModule(files);
  const knownPaths = new Set(files.map(f => f.path));
  const edges: ImportEdge[] = [];

  for (const f of files) {
    if (!f.content) continue;
    const fromMod = topLevelModule(f.path);
    for (const spec of extractImports(f.content)) {
      const resolved = resolveImport(spec, f.path, knownPaths);
      if (!resolved) continue;
      const toMod = topLevelModule(resolved);
      edges.push({
        from: f.path,
        to: resolved,
        crossModule: fromMod !== toMod,
      });
    }
  }

  return { modules, edges, totalFiles: files.length };
}

/**
 * Filtrera ModuleGraph till de moduler/filer som matchar en pathPrefix
 * (t.ex. "src/lib/aiSkills" → bara skills-filer). Tom prefix → oförändrad.
 */
export function filterByPath(graph: ModuleGraph, pathPrefix: string): ModuleGraph {
  if (!pathPrefix) return graph;
  const norm = pathPrefix.replace(/\/$/, '');
  const passes = (p: string) => p.startsWith(norm) || p.startsWith(`./${norm}`);
  const modules = graph.modules
    .map(m => ({ ...m, files: m.files.filter(f => passes(f.path)) }))
    .filter(m => m.files.length > 0);
  const allowed = new Set(modules.flatMap(m => m.files.map(f => f.path)));
  return {
    modules,
    edges: graph.edges.filter(e => allowed.has(e.from) && allowed.has(e.to)),
    totalFiles: allowed.size,
  };
}

/**
 * Rank:a moduler efter antal filer + (in-edges + out-edges)/2.
 * Toppen rankas högst — användbart för att välja den primära modulen
 * att placera centralt i diagrammet.
 */
export function rankModules(graph: ModuleGraph): Array<{ module: string; score: number }> {
  const inOutByMod = new Map<string, { in: number; out: number }>();
  for (const m of graph.modules) {
    inOutByMod.set(m.module, { in: 0, out: 0 });
  }
  for (const e of graph.edges) {
    if (!e.crossModule) continue;
    const fromMod = topLevelModule(e.from);
    const toMod = topLevelModule(e.to);
    const fromBucket = inOutByMod.get(fromMod);
    const toBucket = inOutByMod.get(toMod);
    if (fromBucket) fromBucket.out++;
    if (toBucket) toBucket.in++;
  }
  return graph.modules.map(m => {
    const io = inOutByMod.get(m.module) ?? { in: 0, out: 0 };
    return {
      module: m.module,
      score: m.files.length + (io.in + io.out) / 2,
    };
  }).sort((a, b) => b.score - a.score);
}
